import { useAuth0 } from '@auth0/auth0-react'
import { useMemo } from 'react'

import { analytics } from '~/core/analytics'

function createMetrics(_userId: string, email: string, orgId: string) {
  // Avoid leaking the full email if it's malformed (e.g. missing "@").
  const lastAt = email.lastIndexOf('@')
  const emailHost =
    lastAt >= 0 && lastAt < email.length - 1 ? email.substring(lastAt + 1) : 'unknown'
  const baseProps = { orgId, emailHost }

  return {
    dashboard: {
      filterApplied: (valueLength: number) => {
        analytics.track('Dashboard Filter Applied', {
          ...baseProps,
          field: 'barcode',
          valueLength,
        })
      },
      filterCleared: (previousValueLength: number) => {
        analytics.track('Dashboard Filter Cleared', {
          ...baseProps,
          field: 'barcode',
          previousValueLength,
        })
      },
      plateClick: (plateId: string) => {
        analytics.track('Dashboard Plate Clicked', { ...baseProps, plateId })
      },
    },
    plate: {
      observationNavigate: (plateId: string, direction: 'prev' | 'next') => {
        analytics.track('Plate Observation Navigated', {
          ...baseProps,
          plateId,
          direction,
        })
      },
      wellClick: (plateId: string, well: string) => {
        analytics.track('Plate Well Clicked', { ...baseProps, plateId, well })
      },
    },
    well: {
      observationNavigate: (cultureId: string, direction: 'prev' | 'next') => {
        analytics.track('Well Observation Navigated', {
          ...baseProps,
          cultureId,
          direction,
        })
      },
      wellNavigate: (plateId: string, fromWell: string, toWell: string) => {
        analytics.track('Well Navigated', { ...baseProps, plateId, fromWell, toWell })
      },
      cultureStatusChange: (
        cultureId: string,
        fromStatus: string,
        toStatus: string,
      ) => {
        analytics.track('Culture Status Changed', {
          ...baseProps,
          cultureId,
          fromStatus,
          toStatus,
        })
      },
      zoomPan: (cultureId: string) => {
        analytics.track('Well Image Zoomed or Panned', { ...baseProps, cultureId })
      },
      shareUrl: (cultureId: string) => {
        analytics.track('Well URL Shared', { ...baseProps, cultureId })
      },
      lineageOpen: (cultureId: string) => {
        analytics.track('Culture Lineage Opened', { ...baseProps, cultureId })
      },
    },
    montageImages: {
      open: (cultureId: string, montageId: string) => {
        analytics.track('Montage Images Opened', { ...baseProps, cultureId, montageId })
      },
      select: (cultureId: string, montageId: string, image: number) => {
        analytics.track('Montage Image Selected', {
          ...baseProps,
          cultureId,
          montageId,
          imageIndex: image,
        })
      },
    },
  }
}

const NOOP_METRICS = {
  dashboard: {
    filterApplied: () => {},
    filterCleared: () => {},
    plateClick: () => {},
  },
  plate: {
    observationNavigate: () => {},
    wellClick: () => {},
  },
  well: {
    observationNavigate: () => {},
    wellNavigate: () => {},
    cultureStatusChange: () => {},
    zoomPan: () => {},
    shareUrl: () => {},
    lineageOpen: () => {},
  },
  montageImages: {
    open: () => {},
    select: () => {},
  },
} as ReturnType<typeof createMetrics>

export type Metrics = ReturnType<typeof createMetrics>

export function useMetrics() {
  const { user } = useAuth0()
  const sub = user?.sub
  const email = user?.email
  const orgId = user?.org_id
  return useMemo(
    () => (sub && email && orgId ? createMetrics(sub, email, orgId) : NOOP_METRICS),
    [sub, email, orgId],
  )
}
